import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { Affix, Button, Typography, Modal, Form, Input, Checkbox, Select, Spin, message, Tooltip, Drawer, InputNumber, Switch, ColorPicker } from 'antd'
import { PlusOutlined, SettingOutlined, CloseOutlined, TableOutlined, DeleteOutlined } from '@ant-design/icons'
import { Responsive, WidthProvider } from 'react-grid-layout';
import RenderStandardComponent from '../GridLayout/RenderStandardComponent';
import RenderCustomComponent from '../GridLayout/RenderCustomComponent';
import FiltersDynamic from '../Components/FiltersDynamicComponent';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';

const { Title } = Typography;
const { Option } = Select;
const ResponsiveGridLayout = WidthProvider(Responsive);
const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 320 };
const cols = { lg: 18, md: 18, sm: 9, xs: 6, xxs: 3 };
const rowHeight = 5;

// Default table properties
const defaultTableProperties = {
  bordered: false,
  striped: false,
  size: 'middle', // 'large', 'middle', 'small'
  showHeader: true,
  pagination: {
    position: ['bottomRight'],
    showSizeChanger: true,
    showQuickJumper: false,
    showTotal: true,
    pageSize: 10,
    pageSizeOptions: ['10', '20', '50', '100']
  },
  scroll: {
    x: 'max-content',
    y: 240
  },
  rowSelection: false,
  loading: false,
  sticky: false,
  virtual: false,
  rowClassName: '',
  headerStyle: {
    backgroundColor: '#fafafa',
    fontWeight: 'bold'
  },
  cellPadding: 'default' // 'compact', 'default', 'comfortable'
};

const GridLayout = () => {
  const [components, setComponents] = useState([]);
  const [layout, setLayout] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentComponentName, setCurrentComponentName] = useState('');
  const [currentComponentId, setCurrentComponentId] = useState('');
  const [componentType, setComponentType] = useState('custom');
  const [isStatic, setIsStatic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isUnsavedChangesModalVisible, setIsUnsavedChangesModalVisible] = useState(false);
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [selectedComponentName, setSelectedComponentName] = useState('');
  const [customComponentNames, setCustomComponentNames] = useState([]);
  const [relatedListNames, setRelatedListNames] = useState([]);
  const [selectedRelatedList, setSelectedRelatedList] = useState('');
  const [relatedLists, setRelatedLists] = useState([]);
  const [gridLayoutName, setGridLayoutName] = useState('');
  const [gridLayoutData, setGridLayoutData] = useState({});
  const [metadata, setMetadata] = useState({});
  const [highlightLogic, setHighlightLogic] = useState('AND');
  const [highlightFilters, setHighlightFilters] = useState([]);
  const [highlightValueOperator, setHighlightValueOperator] = useState({});
  const [highlightMessage, setHighlightMessage] = useState('');
  const [multipleHighlights, setMultipleHighlights] = useState([]);
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(-1);
  const [highlightColor, setHighlightColor] = useState('#fff2e6');
  const [highlightBorderColor, setHighlightBorderColor] = useState('#ff8c00');
  const [highlightTextColor, setHighlightTextColor] = useState('#000000');
  const [highlightBold, setHighlightBold] = useState(false);
  const [record, setRecord] = useState({});
  const [layoutLoading, setLayoutLoading] = useState(false);
  const initializingRef = useRef(false);
  const [componentAdded, setComponentAdded] = useState(null);
  
  // Table Properties Configuration State
  const [isTablePropsDrawerVisible, setIsTablePropsDrawerVisible] = useState(false);
  const [currentTableComponentId, setCurrentTableComponentId] = useState('');
  const [tableProperties, setTableProperties] = useState(defaultTableProperties);
  const [isTablePropsLoading, setIsTablePropsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [recordPathField, setRecordPathField] = useState(null);
  const [currentSelectedValue,setCurrentSelectedValue] = useState([]); 
  const [isValueUpdated, setIsValueUpdated] = useState(false);
  const [picklistValues, setPicklistValues] = useState([]); // For picklist values
  const [picklistFieldNameForRecordPath, setPicklistFieldNameForRecordPath] = useState(''); // For field API name
  // Component-specific table properties mapping
  const [componentTableProperties, setComponentTableProperties] = useState({});
  
  const navigate = useNavigate();
  const location = useLocation();
  const { objectName } = useParams();
  const { recordId } = location.state || '';
  const functionRef = useRef(null);
  const standardComponentOptions = [
    { label: 'Record Details', value: 'RecordDetails' },
    { label: 'Record Detail with Tabs', value: 'RecordDetailTabs' },
    { label: 'Compact Layout', value: 'CompactLayout' },
    { label: 'Related Lists', value: 'relatedLists' },
    { label: 'Record Path', value: 'RecordPath' },
    metadata?.allowApprovals && { label: 'My Approvals', value: 'ApprovalDashboard' },
    metadata?.allowActivity && { label: 'My Tasks', value: 'MyTasks' },
    metadata?.allowActivity && { label: 'Activity', value: 'Activity' },
    metadata?.allowComments && { label: 'Comments', value: 'Comments' },
    { label: 'Alerts', value: 'Alerts' },
  ].filter(Boolean);

    useEffect(() => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth"
        });
    }, [componentAdded]);

  useEffect(() => {
    const fetchGridLayout = async () => {
        try {
            const gridLayout = new ApiService(
                `${BASE_URL}/get_grid_layout_by_object/${objectName}`,
                {},
                'GET'
            );
            const response = await gridLayout.makeCall();
            console.log('grid response 1', JSON.stringify(response));
            setGridLayoutData(response);
            
            const mappedLayout = response?.grid_layout?.map((item) => ({
                ...item.position,
                i: item.position.i.toString(),
                static: item.position.static || false,
            }));
    
            const mappedComponents = response?.components?.map((item) => ({
                ...item.info,
            }));
    
            setComponents(mappedComponents);
            setLayout(mappedLayout);
            setGridLayoutName(response?.name);
            setIsDefault(response?.isDefault);
        } catch (error) {
            console.error('No grid layout found:', error);
        }
    };
    fetchGridLayout();
  }, [navigate, objectName]);

  // Initialize component table properties when components are loaded
  useEffect(() => {
    if (components && components.length > 0) {
      const newComponentTableProperties = {};
      components.forEach(component => {
        if (component.selectedComponentName === 'relatedLists' && component.tableProperties) {
          newComponentTableProperties[component.id] = component.tableProperties;
        }
      });
      setComponentTableProperties(newComponentTableProperties);
    }
  }, [components]);

  useEffect(() => {
      if (record && recordPathField && picklistValues.length > 0) {
        const fieldMeta = metadata?.fields.find(f => f._id === recordPathField);
        const fieldApiName = fieldMeta?.name;
        // const fieldMeta = fields.find(f => f._id === recordPathField);
        // const fieldApiName = fieldMeta?.name;
        
        setCurrentSelectedValue(record[fieldApiName]);
        console.log('fieldApiName::', fieldApiName);
  
  
        // setCurrentFieldApiName(fieldApiName);
        const currentValue = record[fieldApiName];
        console.log('record:', record);
        console.log('currentValue:', currentValue);
        const index = picklistValues.findIndex((val) => val === currentValue);
        if (index !== -1) {
          setSelectedIndex(index);
        } else {
          setSelectedIndex(null);
        }
      }
    }, [record, recordPathField, picklistValues, metadata?.fields]);

  const addComponent = () => {
    const newCompId = `${Date.now()}`;
    const newWidget = {
        id: newCompId,
        content: 'Custom Component',
        type: null,
    };
    const newLayoutItem = {
        i: newCompId,
        x: 0,
        y: Infinity,
        w: 9,
        h: 42,
        static: false,
    };
    const updatedComponents = Array.isArray(components) ? components : [];
    const updatedLayout = Array.isArray(layout) ? layout : [];

    // saveHistory('add');
    setComponents([...updatedComponents, newWidget]);
    setLayout([...updatedLayout, newLayoutItem]);
    setComponentAdded(newWidget);
  };


  const updateStatus = async (targetIndex) => {
    // setLoading(true);
    setIsValueUpdated(false);  
    try {
      if (targetIndex >= picklistValues.length || targetIndex < 0) {
        message.error('No more steps to complete');
        return;
      }
  
      const newStatus = picklistValues[targetIndex];
      const fieldMeta = metadata?.fields.find(f => f._id === recordPathField);
      const fieldApiName = fieldMeta?.name;
  
      if (!fieldApiName) {
        message.error("Field API name not found for record path field");
        return;
      }
  
      const body = {
        object_name: objectName,
        data: {
          _id: record._id || "",
          [fieldApiName]: newStatus,
        },
      };
  
    
      const apiService = new ApiService(
        `${BASE_URL}/insert_or_update_records`,
        { 'Content-Type': 'application/json' },
        'POST',
        body
      );
      await apiService.makeCall();
  
      completeStep(newStatus);
      await fetchRecords();
  
      message.success('Status updated successfully');
    } catch (error) {
      message.error('Error updating status');
    } finally {
    //   setLoading(false);
      setSelectedIndex(null);
    }
  };

  const completeStep = (newStatus) => {
    setIsValueUpdated(true);
  }

  const openModal = (id, content) => {
    const component = components.find(comp => comp.id === id);
    setCurrentComponentName(content);
    setCurrentComponentId(id);
    setComponentType(component.componentType || 'custom');
    setSelectedComponentName(component.selectedComponentName || '');
    setSelectedRelatedList(component.selectedRelatedList || '');
    setIsStatic(component.static || false);
setHighlightFilters(
  component.highlightFilters
    ? Object.values(component.highlightFilters).map(f => {
        if (f && typeof f.value === 'object' && f.value !== null) {
          const operator = Object.keys(f.value)[0];
          const val = f.value[operator] !== undefined && f.value[operator] !== null ? String(f.value[operator]) : '';
          return {
            field: f.field,
            value: val,
            displayValue: val,
            operator: operator,
          };
        } else if (f) {
          const val = f.value !== undefined && f.value !== null ? String(f.value) : '';
          return {
            field: f.field,
            value: val,
            displayValue: val,
            operator: 'equal',
          };
        } else {
          return { field: '', value: '', displayValue: '', operator: 'equal' };
        }
      })
    : []
);
    setHighlightLogic(component.filterLogic || 'AND');
    setHighlightValueOperator(component.highlightValueOperator || {});
    setHighlightMessage(component.highlightMessage || '');
    setHighlightColor(component.highlightColor || '#fff2e6');
    setHighlightBorderColor(component.highlightBorderColor || '#ff8c00');
    setHighlightTextColor(component.highlightTextColor || '#000000');
    setHighlightBold(component.highlightBold || false);
    
    // Load multiple highlights or initialize with empty array
    setMultipleHighlights(component.multipleHighlights || []);
    setCurrentHighlightIndex(-1); // No highlight selected initially
    
    setIsModalVisible(true);
  };

  const deleteComponent = (id, event) => {
    event.stopPropagation();
    setComponents(components.filter(component => component.id !== id));
    setLayout(layout.filter(item => item.i !== id));
  }

  const handleOk = () => {
    // Update current highlight if one is selected
    updateCurrentHighlight();
    console.log('Saving highlights to component:', multipleHighlights);
    setComponents(prevComponents => prevComponents.map(component => 
        component.id === currentComponentId ? { 
            ...component, 
            static: isStatic,
            componentType: componentType,
            selectedComponentName: selectedComponentName,
            selectedRelatedList: selectedRelatedList,
            multipleHighlights: multipleHighlights,
        } : component
    ));

    setLayout(prevLayout => prevLayout.map(item => 
        item.i === currentComponentId ? { 
            ...item, 
            static: isStatic, // Update static property
        } : item
    ));
    
    setIsModalVisible(false);
    setSelectedComponentName('');
    setComponentType('custom');
    setIsStatic(false);
    setSelectedRelatedList('');
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedComponentName('');
    setComponentType('custom');
    setIsStatic(false);
    setSelectedRelatedList('');
    setHighlightFilters([]);
    setHighlightLogic('AND');
    setHighlightValueOperator({});
    setHighlightMessage('');
    setHighlightColor('#fff2e6');
    setHighlightBorderColor('#ff8c00');
    setHighlightTextColor('#000000');
    setHighlightBold(false);
    setMultipleHighlights([]);
    setCurrentHighlightIndex(-1);
  };

  const openTablePropsDrawer = (componentId) => {
    // If drawer is already open and switching to different component, show loading
    const isSwitchingComponents = isTablePropsDrawerVisible && currentTableComponentId !== componentId;
    
    if (isSwitchingComponents) {
      setIsTablePropsLoading(true);
    }
    
    setCurrentTableComponentId(componentId);
    
    // Load existing table properties from component if they exist, otherwise use defaults
    const component = components.find(comp => comp.id === componentId);
    let componentProperties = defaultTableProperties;
    
    if (component && component.tableProperties) {
      componentProperties = component.tableProperties;
    } else if (componentTableProperties[componentId]) {
      componentProperties = componentTableProperties[componentId];
    }
    
    // Simulate loading delay when switching components
    if (isSwitchingComponents) {
      setTimeout(() => {
        setTableProperties(componentProperties);
        setIsTablePropsLoading(false);
      }, 300); // Short delay to show loading
    } else {
      setTableProperties(componentProperties);
    }
    
    // Update the mapping for this component
    setComponentTableProperties(prev => ({
      ...prev,
      [componentId]: componentProperties
    }));
    
    setIsTablePropsDrawerVisible(true);
  };

  const closeTablePropsDrawer = () => {
    setIsTablePropsDrawerVisible(false);
    setCurrentTableComponentId('');
    setIsTablePropsLoading(false);
    
    // Reset table properties to defaults when closing without saving
    setTableProperties(defaultTableProperties);
  };

  const updateTableProperty = (key, value) => {
    const newProperties = {
      ...tableProperties,
      [key]: value
    };
    setTableProperties(newProperties);
    
    // Update component-specific properties in real-time
    if (currentTableComponentId) {
      setComponentTableProperties(prev => ({
        ...prev,
        [currentTableComponentId]: newProperties
      }));
      
      // Update the component in components array for real-time UI update
      setComponents(prevComponents => prevComponents.map(component => 
        component.id === currentTableComponentId ? { 
          ...component, 
          tableProperties: newProperties
        } : component
      ));
    }
  };

  const updateNestedTableProperty = (parentKey, childKey, value) => {
    const newProperties = {
      ...tableProperties,
      [parentKey]: {
        ...tableProperties[parentKey],
        [childKey]: value
      }
    };
    setTableProperties(newProperties);
    
    // Update component-specific properties in real-time
    if (currentTableComponentId) {
      setComponentTableProperties(prev => ({
        ...prev,
        [currentTableComponentId]: newProperties
      }));
      
      // Update the component in components array for real-time UI update
      setComponents(prevComponents => prevComponents.map(component => 
        component.id === currentTableComponentId ? { 
          ...component, 
          tableProperties: newProperties
        } : component
      ));
    }
  };

  const saveTableProperties = () => {
    if (!currentTableComponentId) {
      message.error('No component selected for table configuration');
      return;
    }

    // Update the component with table properties
    setComponents(prevComponents => prevComponents.map(component => 
      component.id === currentTableComponentId ? { 
        ...component, 
        tableProperties: tableProperties
      } : component
    ));

    // Update the component-specific mapping
    setComponentTableProperties(prev => ({
      ...prev,
      [currentTableComponentId]: tableProperties
    }));

    message.success('Table configuration saved successfully!');
    closeTablePropsDrawer();
  };

  const handleComponentTypeChange = (value) => {
    setComponentType(value);
  };

  const handleComponentChange = (value) => {
    setSelectedComponentName(value);
  };

  const handleLayoutChange = (newLayout) => {
    setLayout(newLayout);
  };

  // Helper functions for managing multiple highlights
  const addNewHighlight = () => {
    const newHighlight = {
      id: Date.now(),
      name: `Highlight ${multipleHighlights.length + 1}`,
      filters: {},
      filterLogic: 'AND',
      message: '',
      backgroundColor: '#fff2e6',
      borderColor: '#ff8c00',
      textColor: '#000000',
      bold: false
    };
    
    const updatedHighlights = [...multipleHighlights, newHighlight];
    setMultipleHighlights(updatedHighlights);
    setCurrentHighlightIndex(updatedHighlights.length - 1);
    
    // Reset current form fields to new highlight values
    setHighlightFilters([]);
    setHighlightLogic('AND');
    setHighlightMessage('');
    setHighlightColor('#fff2e6');
    setHighlightBorderColor('#ff8c00');
    setHighlightTextColor('#000000');
    setHighlightBold(false);
    setHighlightValueOperator({});
  };

  const selectHighlight = (index) => {
    if (index >= 0 && index < multipleHighlights.length) {
      const highlight = multipleHighlights[index];
      setCurrentHighlightIndex(index);
      
      // Load highlight data into form fields
      const filters = highlight.filters ? Object.values(highlight.filters).map(f => {
        if (f && typeof f.value === 'object' && f.value !== null) {
          const operator = Object.keys(f.value)[0];
          const val = f.value[operator] !== undefined && f.value[operator] !== null ? String(f.value[operator]) : '';
          return {
            field: f.field,
            value: val,
            displayValue: val,
            operator: operator,
          };
        } else if (f) {
          const val = f.value !== undefined && f.value !== null ? String(f.value) : '';
          return {
            field: f.field,
            value: val,
            displayValue: val,
            operator: 'equal',
          };
        } else {
          return { field: '', value: '', displayValue: '', operator: 'equal' };
        }
      }) : [];
      
      setHighlightFilters(filters);
      setHighlightLogic(highlight.filterLogic || 'AND');
      setHighlightMessage(highlight.message || '');
      setHighlightColor(highlight.backgroundColor || '#fff2e6');
      setHighlightBorderColor(highlight.borderColor || '#ff8c00');
      setHighlightTextColor(highlight.textColor || '#000000');
      setHighlightBold(highlight.bold || false);
      setHighlightValueOperator({});
    }
  };

  const updateCurrentHighlight = () => {
    console.log('Updating highlight at index:', currentHighlightIndex);
  console.log('Current highlightFilters:', highlightFilters);
  console.log('Current highlightLogic:', highlightLogic);
  console.log('Current highlightMessage:', highlightMessage);
  console.log('Current highlightColor:', highlightColor);

  // ...existing code...

 
    if (currentHighlightIndex >= 0 && currentHighlightIndex < multipleHighlights.length) {
      const filterObj = highlightFilters.reduce((acc, filter, index) => {
        if (filter.field) {
          acc[index + 1] = {
            field: filter.field,
            value: filter.operator && (filter.value === undefined || filter.value === "")
              ? { [filter.operator]: "" }
              : (filter.value === undefined ? "" : filter.value)
          };
        }
        
//   setMultipleHighlights(updatedHighlights);
        return acc;
      }, {});
      
      let filterLogic = '';
      const filterObjKeys = Object.keys(filterObj);
      if (filterObjKeys.length === 1) {
        filterLogic = '1';
      } else if (filterObjKeys.length > 1) {
        filterLogic = highlightLogic || '';
      }

      const updatedHighlights = [...multipleHighlights];
      updatedHighlights[currentHighlightIndex] = {
        ...updatedHighlights[currentHighlightIndex],
        filters: filterObj,
        filterLogic: filterLogic,
        message: highlightMessage,
        backgroundColor: highlightColor,
        borderColor: highlightBorderColor,
        textColor: highlightTextColor,
        bold: highlightBold
      };
      
      setMultipleHighlights(updatedHighlights);
    }
  };

  const deleteHighlight = (index) => {
    const updatedHighlights = multipleHighlights.filter((_, i) => i !== index);
    setMultipleHighlights(updatedHighlights);
    
    if (currentHighlightIndex === index) {
      setCurrentHighlightIndex(-1);
      // Reset form fields
      setHighlightFilters([]);
      setHighlightLogic('AND');
      setHighlightMessage('');
      setHighlightColor('#fff2e6');
      setHighlightBorderColor('#ff8c00');
      setHighlightTextColor('#000000');
      setHighlightBold(false);
      setHighlightValueOperator({});
    } else if (currentHighlightIndex > index) {
      setCurrentHighlightIndex(currentHighlightIndex - 1);
    }
  };

  // Function to generate row styles based on highlight configuration
  // This function evaluates highlight filters and returns inline CSS styles for table rows
  // Usage: When configuring a related list component, you can set:
  // - Multiple highlight rules with different conditions and colors
  // - Highlight filters (field conditions)
  // - Highlight colors (background, border, text)
  // - Highlight styling (bold text)
  // Example: Status = "Open" -> Red-orange background, Status = "Closed" -> Green background
  const getRowStyle = (record, component) => {
    console.log('getRowStyle called for record:', record);
  console.log('Component multipleHighlights:', component.multipleHighlights);
    if (!component.multipleHighlights || !record || !Array.isArray(component.multipleHighlights)) {
      return {};
    }

    // Check each highlight rule and return the first matching one
    for (const highlight of component.multipleHighlights) {
      if (!highlight.filters || Object.keys(highlight.filters).length === 0) {
        continue;
      }

      const filters = Object.values(highlight.filters);
      const logic = highlight.filterLogic || 'AND';
      
      // Check if record matches highlight conditions
      const matchResults = filters.map(filter => {
        if (!filter.field || filter.field === '') return false;
        
        const recordValue = record[filter.field];
        const filterValue = typeof filter.value === 'object' 
          ? Object.values(filter.value)[0] 
          : filter.value;
        
        if (typeof filter.value === 'object' && filter.value !== null) {
          const operator = Object.keys(filter.value)[0];
          switch (operator) {
            case 'equal':
              return recordValue === filterValue;
            case 'not_equal':
              return recordValue !== filterValue;
            case 'contains':
              return recordValue && recordValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
            case 'not_contains':
              return !recordValue || !recordValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
            case 'starts_with':
              return recordValue && recordValue.toString().toLowerCase().startsWith(filterValue.toString().toLowerCase());
            case 'ends_with':
              return recordValue && recordValue.toString().toLowerCase().endsWith(filterValue.toString().toLowerCase());
            case 'greater_than':
              return Number(recordValue) > Number(filterValue);
            case 'less_than':
              return Number(recordValue) < Number(filterValue);
            case 'greater_equal':
              return Number(recordValue) >= Number(filterValue);
            case 'less_equal':
              return Number(recordValue) <= Number(filterValue);
            default:
              return recordValue === filterValue;
          }
        } else {
          return recordValue === filterValue;
        }
      });

      // Apply logic (AND/OR)
      let matches = false;
      if (logic === 'AND') {
        matches = matchResults.length > 0 && matchResults.every(result => result === true);
      } else if (logic === 'OR') {
        matches = matchResults.some(result => result === true);
      } else if (logic === '1' && matchResults.length === 1) {
        matches = matchResults[0];
      }

      // Return highlight style if matches (first match wins)
      if (matches) {
        return {
          style: {
            backgroundColor: highlight.backgroundColor,
            border: `1px solid ${highlight.borderColor}`,
            color: highlight.textColor,
            fontWeight: highlight.bold ? 'bold' : 'normal'
          },
          message: highlight.message || null
        };
      }
    }
    return { style: {}, message: null };
  };

  // const fetchCustomComponents = useCallback(async () => {
  //   try {
  //       // const apiService = new ApiService(`${BASE_URL}/get_custom_components`, {}, 'GET');
  //       // const responseData = await apiService.makeCall();
  //       // console.log('responseData', responseData);

  //       // Simulating API call
  //       await new Promise(resolve => setTimeout(resolve, 1000));
    
  //       const responseData = ['CustomComp'];
        
  //       setCustomComponentNames(responseData);
  //     } catch (error) {
  //       console.error('Error fetching custom components', error);
  //     }
  // }, []);
  const fetchCustomComponents = useCallback(async () => {
    try {
        const res = await fetch(`${BASE_URL}/plugins/get_names`);
        const names = await res.json();   
        setCustomComponentNames(names);
      } catch (error) {
        console.error('Error fetching custom components', error);
      }
  }, []);

    const fetchRelatedRecords = useCallback(async () => {
        try {
            const relatedListService = new ApiService(
                `${BASE_URL}/related_lists/for_object/${objectName}`,
                {},
                'GET'
            );
            const response = await relatedListService.makeCall();

            const uniqueRelatedListNames = Array.isArray(response) && response.length > 0
                ? Array.from(new Set(response.map(record => ({
                    id: record.related_list._id,
                    label: record.related_list.related_list_name
                }))))
                : [];

            const additionalListNames = [];
            if (metadata?.trackFieldHistory) {
                additionalListNames.push({ id: 'FieldHistory', label: 'Field History' });
            }
            if (metadata?.allowFiles) {
                additionalListNames.push({ id: 'Attachments', label: 'Attachments' });
            }

            setRelatedLists(response || []);
            setRelatedListNames([...uniqueRelatedListNames, ...additionalListNames]);

        } catch (err) {
            console.error('Error fetching related records', err);
        }
    }, [objectName, metadata?.trackFieldHistory, metadata?.allowFiles]);

  const handleSave = async () => {
    const uniqueId = Date.now();
    const gridData = {
        name: gridLayoutName || `${objectName} Grid Layout #${uniqueId}`,
        grid_layout: layout.map(item => ({ position: item })),
        objectName: objectName,
        components: components.map(item => ({
            info: item,
            name: item.name,
        })),
        isDefault: isDefault,
    };

    const payload = {
        grid_layout: gridData
    };

    console.log('Grid Layout Payload:', JSON.stringify(payload));

    try {
        console.log('Payload:', JSON.stringify(payload));            
        const isUpdate = gridLayoutData?._id !== undefined;
        const apiService = new ApiService(
            `${BASE_URL}/${isUpdate ? `update_grid_layout/${gridLayoutData?._id}` : 'create_grid_layout'}`,
            { 'Content-Type': 'application/json' },
            isUpdate ? 'PATCH' : 'POST',
            JSON.stringify(payload)
        );
        await apiService.makeCall(navigate);
        message.success('Grid Layout saved successfully!');
        setIsUnsavedChangesModalVisible(false);
        setIsSettingsModalVisible(false);
        navigate(-1);
    } catch (error) {
        console.error('Error saving Grid Layout:', error);
        message.error('Error saving Grid Layout!');
    }
};

  const fetchMetadata = useCallback(async () => {
    try {
      const [objectResponse, fieldsResponse] = await Promise.all([
        new ApiService(`${BASE_URL}/get_mt_object_by_name/${objectName}`, {}, 'GET').makeCall(),
        new ApiService(`${BASE_URL}/mt_fields/object/${objectName}`, {}, 'GET').makeCall()
      ]);

      // Try to fetch compact layout separately and handle failure gracefully
      let layoutResponse = null;
      try {
        layoutResponse = await new ApiService(`${BASE_URL}/get_compact_layout/${objectName}`, {}, 'GET').makeCall();
      } catch (layoutError) {
        console.warn('Compact layout not found for object:', objectName, layoutError);
        // Continue without compact layout
      }

      // Extract record path field with safe fallback
      const recordPathFieldId = layoutResponse?.record_path_field || layoutResponse?.[0]?.record_path_field || null;

      setMetadata({
        allowApprovals: objectResponse[0]?.allow_approvals,
        allowActivity: objectResponse[0]?.track_activities,
        trackFieldHistory: objectResponse[0]?.track_field_history,
        allowFiles: objectResponse[0]?.allow_files,
        creationView: objectResponse[0]?.record_creation_view,
        fields: fieldsResponse,
        recordPathField: recordPathFieldId,
        picklistValues: recordPathFieldId ? 
          (fieldsResponse.find((field) => field._id === recordPathFieldId)?.picklist_values || []) : [],
        compactLayout: layoutResponse || layoutResponse?.[0] || null,
        layout: [],
        actionButtons: [],
        allowComments: objectResponse[0]?.allow_comments,
      });

      setRecordPathField(recordPathFieldId);

      if (recordPathFieldId) {
        const picklistField = fieldsResponse.find(field => field?._id === recordPathFieldId);
        setPicklistValues(picklistField?.picklist_values || []);
        setPicklistFieldNameForRecordPath(picklistField?.name || '');
        console.log('picklistValues::', picklistField?.picklist_values || []);
      } else {
        // Reset picklist-related states when no record path field is found
        setPicklistValues([]);
        setPicklistFieldNameForRecordPath('');
        console.log('No record path field found, resetting picklist values');
      }

    } catch (error) {
      console.error('Error fetching metadata', error);
      // Even if there's an error, try to set minimal metadata to allow other components to work
      setMetadata({
        allowApprovals: false,
        allowActivity: false,
        trackFieldHistory: false,
        allowFiles: false,
        creationView: null,
        fields: [],
        recordPathField: null,
        picklistValues: [],
        compactLayout: null,
        layout: [],
        actionButtons: [],
        allowComments: false,
      });
    }
  }, [objectName]);

useEffect(() => {
    fetchMetadata();
  }, [objectName, fetchMetadata]);

    const fetchRecords = useCallback(async() => {
      try{
        const apiService = new ApiService(`${BASE_URL}/fetch_single_record/${objectName}/${recordId}`, {}, 'GET');
        const responseData = await apiService.makeCall();
        console.log("res##", responseData);
        setRecord(responseData);
        return responseData;
      }
      catch(error){
        console.error('Error fetching record:', error);
        return null;
      }
    }, [objectName, recordId]);

    const fetchLayoutNActionButtons = useCallback(async (recordData = null) => {
        const currentRecord = recordData || record;
        console.log('record::', currentRecord);
        
        try {
            setLayoutLoading(true);
            let detailLayoutAndActionButtons = [];

            if (currentRecord?.RecordType) {
            console.log('Fetching layout for RecordType:', currentRecord.RecordType);
            const layoutService = new ApiService(
                `${BASE_URL}/get_layout_by_record_type/${objectName}/${currentRecord.RecordType}`,
                {},
                'GET'
            );
            const layoutResponse = await layoutService.makeCall();
            
            detailLayoutAndActionButtons = [layoutResponse];
            } else {
            console.log('Fetching default layout for object:', objectName);
            const fallbackLayoutService = new ApiService(
                `${BASE_URL}/get_by_objectName/${objectName}`,
                {},
                'GET'
            );
            const fallbackResponse = await fallbackLayoutService.makeCall();
            
            detailLayoutAndActionButtons = (Array.isArray(fallbackResponse) ? fallbackResponse : [fallbackResponse])
                .filter(layout => layout.active);
            }
            console.log('finalLayoutAndButtons::', detailLayoutAndActionButtons[0]);
            setMetadata(prevMetadata => ({
            ...prevMetadata,
            layout: detailLayoutAndActionButtons,
            actionButtons: detailLayoutAndActionButtons[0]?.action_button_ids || []
            }));
        } catch (error) {
            console.error('Error fetching layout', error);
        } finally {
            setLayoutLoading(false);
        }
    }, [record, objectName]);

    useEffect(() => {
        const initializeData = async () => {
            if (initializingRef.current) {
            return;
            }
            
            initializingRef.current = true;
            
            try {
            setLoading(true);
            await Promise.all([
                fetchMetadata(),
                fetchRelatedRecords(),
                fetchCustomComponents()
            ]);
            
            const recordData = await fetchRecords();
            
            // Finally fetch layout based on the record data
            if (recordData && Object.keys(recordData).length > 0) {
                await fetchLayoutNActionButtons(recordData);
            } else {
                await fetchLayoutNActionButtons({});
            }
            } catch (error) {
            console.error('Error initializing data:', error);
            } finally {
            // Only set loading to false after ALL operations are complete
            setLoading(false);
            initializingRef.current = false;
            }
        };

        initializeData();
    }, [objectName, recordId, fetchMetadata, fetchRecords, fetchRelatedRecords, fetchLayoutNActionButtons, fetchCustomComponents]);

    if (loading || !metadata) {
        return <Spin />;
    }

    const cancelSelectedValue = () => {
        setIsValueUpdated(false);
        setSelectedIndex(null);
      }
  return (
    <>
        <Affix offsetTop={64}>
            <div style={{
                ...styles.header,
                width: isTablePropsDrawerVisible ? 'calc(100% - 320px)' : '100%',
                transition: 'width 0.3s ease',
            }}>
                <Title level={5} style={styles.headerTitle}>{objectName} Record Page</Title>
                <div style={styles.headerButtons}>
                    <Tooltip title="Adds a component to the bottom. Gravity, right?">
                        <Button type="primary" icon={<PlusOutlined />} onClick={addComponent}>Add</Button>
                    </Tooltip>
                    <Button type="primary" icon={<SettingOutlined />} onClick={() => setIsSettingsModalVisible(true)}></Button>
                    <Button type="primary" onClick={handleSave}>Save</Button>
                </div>
            </div>
        </Affix>
        <div 
            className="grid-canvas-container"
            style={{
                ...styles.parentContainer,
                marginRight: isTablePropsDrawerVisible ? 320 : 0,
                transition: 'margin-right 0.3s ease',
                position: 'relative',
            }}
        >
            <div style={styles.cardsContainer}>
                <ResponsiveGridLayout
                    className="my-1 mx-1"
                    breakpoints={breakpoints}
                    cols={cols}
                    rowHeight={rowHeight}
                    layout={layout}
                    onLayoutChange={handleLayoutChange}
                    draggableCancel=".non-draggable"
                    resizeHandles={['se', 'sw']}
                >
                    {components?.map((component) => (
                        <div
                            className="grid-cell"
                            key={component.id}
                            data-grid={layout.find((item) => item.i === component.id.toString())}
                            style={{ ...styles.gridItem, overflow: 'hidden',
                                        minHeight: 120,
                                        minWidth: 180,
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                        >
                            <div style={styles.gridHeader}>
                                <div></div>
                                <div style={styles.iconContainer}>
                                    {component.selectedComponentName === 'relatedLists' && (
                                        <Tooltip title="Configure Table Properties">
                                            <TableOutlined
                                                className="non-draggable"
                                                style={styles.icon}
                                                onClick={() => openTablePropsDrawer(component.id)}
                                            />
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Configure Component">
                                        <SettingOutlined
                                            className="non-draggable"
                                            style={styles.icon}
                                            onClick={() => openModal(component.id, component.content)}
                                        />
                                    </Tooltip>
                                    <CloseOutlined
                                        className="non-draggable"
                                        style={styles.icon}
                                        onClick={(event) => deleteComponent(component.id, event)}
                                    />
                                </div>
                            </div>
                            <div className='non-draggable' 
                                style={{width: '100%', height: '100%'}}
                            >
                                {component.componentType === 'standard' ? (
                                    <>
                                    {console.log('Rendering standard component', component)}
                                    <RenderStandardComponent 
                                        component={component} 
                                        recordId={recordId} 
                                        objectName={objectName} 
                                        functionRef={functionRef} 
                                        metadata={metadata} 
                                        overallLoading={loading || layoutLoading} 
                                        record={record} 
                                        fetchRecords={fetchRecords} 
                                        setRecord={setRecord}
                                        updateStatus={updateStatus}
                                        picklistValues={picklistValues}
                                        selectedIndex={selectedIndex}
                                        currentSelectedValue={currentSelectedValue}
                                        completeStep={completeStep}
                                        setSelectedIndex={setSelectedIndex}
                                        isValueUpdated={isValueUpdated}
                                        cancelSelectedValue={cancelSelectedValue}
                                        picklistFieldNameForRecordPath={picklistFieldNameForRecordPath}
                                        tableProperties={
                                          component.tableProperties || 
                                          componentTableProperties[component.id] || 
                                          defaultTableProperties
                                        }
                                        getRowStyle={getRowStyle}
                                    />
                                    </>
                                ) : component.componentType === 'custom' ? (
                                    <RenderCustomComponent component={component}  recordId={recordId} objectName={objectName} />
                                ) : (
                                    <div style={styles.placeholder} className="non-draggable">
                                        Please configure your component by clicking on the configure icon.
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </ResponsiveGridLayout>
            </div>
        </div>
        {/* Modal for Configuration */}
        <Modal
                title={`Configure ${currentComponentName}`}
                visible={isModalVisible}
                onOk={handleOk}
                zIndex={1002}   // higher than sidebar
                onCancel={handleCancel}
                bodyStyle={{
                  maxHeight: '60vh', // or a fixed px value like '500px'
                  overflowY: 'auto',
                  padding: '24px',
                }}
                style={{
                  top: 40, // Optional: move modal down from top
                }}
            >
                <Form layout="vertical">
                    <Form.Item label="Component Type">
                        <Select 
                            style={{ width: '100%', marginBottom: '6px' }} 
                            onChange={handleComponentTypeChange}
                            value={componentType}
                            placeholder="Select component type"
                        >
                            <Option value="custom">Custom</Option>
                            <Option value="standard">Standard</Option>
                        </Select>
                    </Form.Item>
                    {componentType === 'standard' ? (
                        <Form.Item label="Standard Component">
                            <Select 
                                placeholder="Select standard component"
                                style={{ width: '100%' }} 
                                onChange={handleComponentChange}
                                value={selectedComponentName}
                            >
                                {standardComponentOptions.map(option => (
                                    <Option key={option.value} value={option.value}>{option.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    ) : (
                        <Form.Item label="Custom Component">
                            <Select 
                                placeholder="Select custom component"
                                style={{ width: '100%' }} 
                                onChange={handleComponentChange}
                                value={selectedComponentName}
                            >
                                {customComponentNames.map(name => (
                                    <Option key={name} value={name}>{name}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    {selectedComponentName === 'relatedLists' && (
                        <Form.Item label="Related List">
                                <Select 
                                    placeholder="Select related list"
                                    style={{ width: '100%' }} 
                                    onChange={(value) => setSelectedRelatedList(value)}
                                    value={selectedRelatedList}
                                >
                                    {relatedListNames.map(name => (
                                        <Option key={name.id} value={name.id}>{name.label}</Option>
                                    ))}
                                </Select>
                        </Form.Item>
                    )}
                    {selectedComponentName === 'relatedLists' && selectedRelatedList &&  (
                        <>
                            <Form.Item label="Row Highlights">
                                <div style={{ marginBottom: '16px' }}>
                                    <Button 
                                        type="dashed" 
                                        onClick={addNewHighlight}
                                        style={{ width: '100%', marginBottom: '12px' }}
                                        icon={<PlusOutlined />}
                                    >
                                        Add New Highlight Rule
                                    </Button>
                                    
                                    {multipleHighlights.length > 0 && (
                                        <div style={{ 
                                            maxHeight: '200px', 
                                            overflowY: 'auto',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '6px',
                                            padding: '8px'
                                        }}>
                                            {multipleHighlights.map((highlight, index) => (
                                                <div 
                                                    key={highlight.id}
                                                    style={{
                                                        padding: '8px',
                                                        marginBottom: '8px',
                                                        border: `2px solid ${currentHighlightIndex === index ? '#1890ff' : '#f0f0f0'}`,
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        backgroundColor: currentHighlightIndex === index ? '#f6ffed' : '#fafafa',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                    onClick={() => selectHighlight(index)}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div 
                                                            style={{
                                                                width: '16px',
                                                                height: '16px',
                                                                backgroundColor: highlight.backgroundColor,
                                                                border: `1px solid ${highlight.borderColor}`,
                                                                borderRadius: '3px'
                                                            }}
                                                        />
                                                        <span style={{ fontWeight: currentHighlightIndex === index ? 'bold' : 'normal' }}>
                                                            {highlight.name}
                                                        </span>
                                                        {highlight.message && (
                                                            <span style={{ fontSize: '12px', color: '#666' }}>
                                                                - {highlight.message}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <Button 
                                                        type="text" 
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteHighlight(index);
                                                        }}
                                                        style={{ color: '#ff4d4f' }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                {currentHighlightIndex >= 0 && (
                                    <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px' }}>
                                        <div style={{ marginBottom: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                            Editing: {multipleHighlights[currentHighlightIndex]?.name}
                                        </div>
                                        
                                        <Form.Item label="Highlight Name" style={{ marginBottom: '12px' }}>
                                            <Input 
                                                placeholder="Enter highlight name" 
                                                value={multipleHighlights[currentHighlightIndex]?.name || ''} 
                                                onChange={(e) => {
                                                    const updatedHighlights = [...multipleHighlights];
                                                    updatedHighlights[currentHighlightIndex].name = e.target.value;
                                                    setMultipleHighlights(updatedHighlights);
                                                }}
                                            />
                                        </Form.Item>
                                    </div>
                                )}
                            </Form.Item>
                            
                            {currentHighlightIndex >= 0 && (
                                <Form.Item label="Highlight Filters">
                                    <FiltersDynamic
                                        fields={
                                            (
                                                relatedLists.find(
                                                    rl =>
                                                        rl.related_list?._id === selectedRelatedList
                                                )?.fields_data || []
                                            )
                                        }
                                        logic={highlightLogic}
                                        setLogic={setHighlightLogic}
                                        filters={highlightFilters}
                                        setFilters={setHighlightFilters}
                                        setValueOperator={setHighlightValueOperator}
                                        valueOperator={highlightValueOperator}
                                        fetchLookupRecordsForExistingFilters={() => {}}
                                        fetchLookupRecords={() => {}}
                                        lookupOptions={{}}
                                        heading={false}
                                    />
                                </Form.Item>
                            )}
                        </>
                    )}
                    {selectedComponentName === 'relatedLists' && selectedRelatedList && currentHighlightIndex >= 0 && (
                        <Form.Item label="Highlight Message">
                            <Input 
                                placeholder="Enter highlight message" 
                                value={highlightMessage || ''} 
                                onChange={(e) => setHighlightMessage(e.target.value)} 
                                style={{ marginBottom: '6px' }} 
                            />
                        </Form.Item>
                    )}
                    {selectedComponentName === 'relatedLists' && selectedRelatedList && currentHighlightIndex >= 0 && (
                        <Form.Item label="Highlight Colors">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ minWidth: '120px' }}>Background Color:</span>
                                    <ColorPicker
                                        value={highlightColor}
                                        onChange={(color) => setHighlightColor(color.toHexString())}
                                        showText
                                        size="small"
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ minWidth: '120px' }}>Border Color:</span>
                                    <ColorPicker
                                        value={highlightBorderColor}
                                        onChange={(color) => setHighlightBorderColor(color.toHexString())}
                                        showText
                                        size="small"
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ minWidth: '120px' }}>Text Color:</span>
                                    <ColorPicker
                                        value={highlightTextColor}
                                        onChange={(color) => setHighlightTextColor(color.toHexString())}
                                        showText
                                        size="small"
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ minWidth: '120px' }}>Bold Text:</span>
                                    <Checkbox
                                        checked={highlightBold}
                                        onChange={(e) => setHighlightBold(e.target.checked)}
                                    />
                                </div>
                                <div style={{ marginTop: '12px' }}>
                                    <Button 
                                        type="primary" 
                                        size="small"
                                        onClick={updateCurrentHighlight}
                                        style={{ width: '100%' }}
                                    >
                                        Update Highlight Rule
                                    </Button>
                                </div>
                            </div>
                        </Form.Item>
                    )}
                    <Form.Item label="Static">
                        <Checkbox
                            style={{ marginBottom: '6px' }}
                            onChange={(e) => setIsStatic(e.target.checked)}
                            checked={isStatic}
                        />
                    </Form.Item>
                </Form>
            </Modal>
            {/* Modal for Unsaved Changes */}
            <Modal
                title="Unsaved Changes"
                visible={isUnsavedChangesModalVisible}
                onCancel={() => setIsUnsavedChangesModalVisible(false)}
                zIndex={1002}   // higher than sidebar
                footer={[
                    <Button key="discard">
                        Discard
                    </Button>,
                    <Button key="save" type="primary">
                        Save
                    </Button>,
                ]}
            >
                <p>Do you want to keep your changes?</p>
            </Modal>
            {/* Modal for Settings */}
            <Modal
                title="Grid Settings"
                visible={isSettingsModalVisible}
                onOk={handleSave}
                zIndex={1002}   // higher than sidebar
                onCancel={() => setIsSettingsModalVisible(false)}
            >
                <Form layout="vertical">
                    <Form.Item label="Grid Name">
                        <Input 
                            placeholder="Enter Grid Name" 
                            value={gridLayoutName} 
                            onChange={(e) => setGridLayoutName(e.target.value)} 
                            style={{ marginBottom: '6px' }} 
                        />
                    </Form.Item>
                    {/* <Form.Item label="Set as Default">
                        <Checkbox
                            style={{ marginBottom: '6px' }}
                            onChange={(e) => setIsDefault(e.target.checked)}
                            checked={isDefault}
                        />
                    </Form.Item> */}
                </Form>
            </Modal>
            
            {/* Table Properties Configuration Drawer */}
            <Drawer
                title="Table Config"
                placement="right"
                width={320}
                onClose={closeTablePropsDrawer}
                open={isTablePropsDrawerVisible}
                destroyOnClose={false}
                mask={false}
                getContainer={() => document.querySelector('.grid-canvas-container') || document.body}
                style={{
                    position: 'absolute',
                    top: 64,
                    height: 'calc(100vh - 64px)',
                    zIndex: 1001,
                    // boxShadow: 'none',
                }}
                bodyStyle={{
                    padding: '16px 12px',
                    height: 'calc(100vh - 119px)',
                    overflowY: 'auto',
                }}
                contentWrapperStyle={{ boxShadow: 'none' }}
            >
                {isTablePropsLoading ? (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '200px',
                        flexDirection: 'column',
                        gap: '16px'
                    }}>
                        <Spin size="large" />
                        <span style={{ color: '#666' }}>Loading table properties...</span>
                    </div>
                ) : (
                    <Form layout="vertical">
                    <Form.Item label="Table Size">
                        <Select
                            value={tableProperties.size}
                            onChange={(value) => updateTableProperty('size', value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="small">Small</Option>
                            <Option value="middle">Middle</Option>
                            <Option value="large">Large</Option>
                        </Select>
                    </Form.Item>
                    
                    <Form.Item label="Table Style">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <Checkbox
                                checked={tableProperties.bordered}
                                onChange={(e) => updateTableProperty('bordered', e.target.checked)}
                            >
                                Bordered
                            </Checkbox>
                            <Checkbox
                                checked={tableProperties.striped}
                                onChange={(e) => updateTableProperty('striped', e.target.checked)}
                            >
                                Striped Rows
                            </Checkbox>
                            <Checkbox
                                checked={tableProperties.showHeader}
                                onChange={(e) => updateTableProperty('showHeader', e.target.checked)}
                            >
                                Show Header
                            </Checkbox>
                            <Checkbox
                                checked={tableProperties.sticky}
                                onChange={(e) => updateTableProperty('sticky', e.target.checked)}
                            >
                                Sticky Header
                            </Checkbox>
                        </div>
                    </Form.Item>

                    <Form.Item label="Scroll Height (px)">
                        <InputNumber
                            value={typeof tableProperties.scroll.y === 'number' ? tableProperties.scroll.y : 240}
                            onChange={(value) => updateNestedTableProperty('scroll', 'y', value)}
                            style={{ width: '100%' }}
                            min={100}
                            max={800}
                        />
                    </Form.Item>

                    <Form.Item label="Pagination">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                                <label>Page Size: </label>
                                <InputNumber
                                    value={tableProperties.pagination.pageSize}
                                    onChange={(value) => updateNestedTableProperty('pagination', 'pageSize', value)}
                                    min={1}
                                    max={1000}
                                />
                            </div>
                            <Checkbox
                                checked={tableProperties.pagination.showSizeChanger}
                                onChange={(e) => updateNestedTableProperty('pagination', 'showSizeChanger', e.target.checked)}
                            >
                                Show Size Changer
                            </Checkbox>
                            <Checkbox
                                checked={tableProperties.pagination.showQuickJumper}
                                onChange={(e) => updateNestedTableProperty('pagination', 'showQuickJumper', e.target.checked)}
                            >
                                Show Quick Jumper
                            </Checkbox>
                            <Checkbox
                                checked={tableProperties.pagination.showTotal}
                                onChange={(e) => updateNestedTableProperty('pagination', 'showTotal', e.target.checked)}
                            >
                                Show Total
                            </Checkbox>
                        </div>
                    </Form.Item>

                    <Form.Item label="Row Selection">
                        <Switch
                            checked={tableProperties.rowSelection}
                            onChange={(checked) => updateTableProperty('rowSelection', checked)}
                        />
                    </Form.Item>

                    <Form.Item label="Virtual Scrolling">
                        <Switch
                            checked={tableProperties.virtual}
                            onChange={(checked) => updateTableProperty('virtual', checked)}
                        />
                    </Form.Item>

                    <Form.Item label="Cell Padding">
                        <Select
                            value={tableProperties.cellPadding}
                            onChange={(value) => updateTableProperty('cellPadding', value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="compact">Compact</Option>
                            <Option value="default">Default</Option>
                            <Option value="comfortable">Comfortable</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="Header Background Color">
                        <ColorPicker
                            value={tableProperties.headerStyle.backgroundColor}
                            onChange={(color) => updateNestedTableProperty('headerStyle', 'backgroundColor', color.toHexString())}
                            showText
                        />
                    </Form.Item>
                    
                    <Form.Item style={{ marginTop: '24px' }}>
                        <Button 
                            type="primary" 
                            onClick={saveTableProperties}
                            style={{ width: '100%' }}
                        >
                            Save Table Configuration
                        </Button>
                    </Form.Item>
                </Form>
                )}
            </Drawer>
    </>
  )
};

const styles = {
    parentContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "#f0f2f5",
        minHeight: "100vh",
        height: "auto",
    },
    header: {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 20px",
        backgroundColor: "#fff",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        zIndex: 1000,
    },
    headerTitle: {
        margin: 0,
    },
    headerButtons: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    cardsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        justifyContent: "center",
        flexGrow: 1,
        height: "auto",
    },
    gridItem: {
        position: 'relative',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '10px',
        backgroundColor: 'transparent',
    },
    gridHeader: {
        position: 'absolute',
        top: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        backgroundColor: 'transparent', // Adjust as needed
        zIndex: 1
    },
    iconContainer: {
        display: 'flex',
        gap: '10px',
        padding: '5px',
    },
    icon: {
        cursor: 'pointer',
    },
    placeholder: {
        color: '#aaa',
        textAlign: 'center',
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
    done: {
        cursor: 'pointer',
        color: '#1890ff',
    },
};

export default GridLayout